import React, { useState, useEffect } from 'react';
import {
  Box, Card, IconButton, Typography, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, CircularProgress
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosConfig';

const TimeTrackerWidget = () => {
  const [activeTimer, setActiveTimer] = useState(null);
  const [currentDuration, setCurrentDuration] = useState(0);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  
  // Form data for starting timer
  const [formData, setFormData] = useState({
    project_id: '',
    task_id: '',
    description: '',
    is_billable: true
  });

  // Check for active timer on mount
  useEffect(() => {
    fetchActiveTimer();
    fetchProjects();
  }, []);

  // Update duration every second if timer is running
  useEffect(() => {
    let interval;
    if (activeTimer && activeTimer.status === 'running') {
      interval = setInterval(() => {
        const started = new Date(activeTimer.started_at);
        const now = new Date();
        const duration = Math.floor((now - started) / 1000) - (activeTimer.total_pause_duration || 0);
        setCurrentDuration(duration);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const fetchActiveTimer = async () => {
    try {
      const response = await axiosInstance.get('/api/time-tracker/active');
      if (response.data.data) {
        setActiveTimer(response.data.data);
        setCurrentDuration(response.data.data.current_duration || 0);
      }
    } catch (error) {
      console.error('Error fetching active timer:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/api/projects');
      setProjects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTasks = async (projectId) => {
    if (!projectId) {
      setTasks([]);
      return;
    }
    try {
      const response = await axiosInstance.get('/api/tasks', {
        params: { project_id: projectId }
      });
      setTasks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleStartTimer = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/time-tracker/start', formData);
      setActiveTimer(response.data.data);
      setShowStartDialog(false);
      setFormData({ project_id: '', task_id: '', description: '', is_billable: true });
    } catch (error) {
      console.error('Error starting timer:', error);
      alert(error.response?.data?.error || 'Error al iniciar timer');
    } finally {
      setLoading(false);
    }
  };

  const handleStopTimer = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`/api/time-tracker/stop/${activeTimer.id}`);
      setActiveTimer(null);
      setCurrentDuration(0);
      setShowStopDialog(false);
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Error al detener timer');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseTimer = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/api/time-tracker/pause/${activeTimer.id}`);
      setActiveTimer(response.data.data);
    } catch (error) {
      console.error('Error pausing timer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResumeTimer = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/api/time-tracker/resume/${activeTimer.id}`);
      setActiveTimer(response.data.data);
    } catch (error) {
      console.error('Error resuming timer:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Floating widget
  if (activeTimer) {
    return (
      <>
        <Card
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            minWidth: 300,
            background: activeTimer.status === 'running' 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
            color: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatDuration(currentDuration)}
                </Typography>
              </Box>
              <Chip
                label={activeTimer.status === 'running' ? 'Corriendo' : 'Pausado'}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white'
                }}
              />
            </Box>

            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
              {activeTimer.project_name || 'Sin proyecto'}
              {activeTimer.task_title && ` • ${activeTimer.task_title}`}
            </Typography>

            {activeTimer.description && (
              <Typography variant="caption" sx={{ display: 'block', mb: 2, opacity: 0.8 }}>
                {activeTimer.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
              {activeTimer.status === 'running' ? (
                <Tooltip title="Pausar">
                  <IconButton
                    onClick={handlePauseTimer}
                    disabled={loading}
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
                  >
                    <PauseIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Reanudar">
                  <IconButton
                    onClick={handleResumeTimer}
                    disabled={loading}
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
                  >
                    <PlayIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              <Button
                variant="contained"
                onClick={() => setShowStopDialog(true)}
                disabled={loading}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: '#667eea',
                  '&:hover': {
                    bgcolor: 'white'
                  }
                }}
                startIcon={<StopIcon />}
              >
                Detener
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Stop Confirmation Dialog */}
        <Dialog open={showStopDialog} onClose={() => setShowStopDialog(false)}>
          <DialogTitle>Detener Timer</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de detener el timer?
            </Typography>
            <Typography variant="h4" sx={{ my: 2, textAlign: 'center', color: 'primary.main' }}>
              {formatDuration(currentDuration)}
            </Typography>
            {activeTimer.hourly_rate && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Costo estimado: ${((currentDuration / 3600) * activeTimer.hourly_rate).toFixed(2)}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowStopDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleStopTimer}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Detener Timer'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Start button when no timer is active
  return (
    <>
      <Tooltip title="Iniciar Timer">
        <IconButton
          onClick={() => setShowStartDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            }
          }}
        >
          <PlayIcon sx={{ fontSize: 32 }} />
        </IconButton>
      </Tooltip>

      {/* Start Timer Dialog */}
      <Dialog 
        open={showStartDialog} 
        onClose={() => setShowStartDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Iniciar Timer
          <IconButton
            onClick={() => setShowStartDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Proyecto (opcional)</InputLabel>
              <Select
                value={formData.project_id}
                onChange={(e) => {
                  setFormData({ ...formData, project_id: e.target.value, task_id: '' });
                  fetchTasks(e.target.value);
                }}
                label="Proyecto (opcional)"
              >
                <MenuItem value="">
                  <em>Sin proyecto</em>
                </MenuItem>
                {projects.map(project => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.project_id && (
              <FormControl fullWidth>
                <InputLabel>Tarea (opcional)</InputLabel>
                <Select
                  value={formData.task_id}
                  onChange={(e) => setFormData({ ...formData, task_id: e.target.value })}
                  label="Tarea (opcional)"
                >
                  <MenuItem value="">
                    <em>Sin tarea específica</em>
                  </MenuItem>
                  {tasks.map(task => (
                    <MenuItem key={task.id} value={task.id}>
                      {task.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="¿En qué estás trabajando?"
            />

            <FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">¿Es facturable?</Typography>
                <Select
                  value={formData.is_billable}
                  onChange={(e) => setFormData({ ...formData, is_billable: e.target.value })}
                  size="small"
                >
                  <MenuItem value={true}>Sí - Facturable</MenuItem>
                  <MenuItem value={false}>No - No facturable</MenuItem>
                </Select>
              </Box>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStartDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleStartTimer}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
          >
            Iniciar Timer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TimeTrackerWidget;

